import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  resolveLocalizedEntry,
  langAttribute,
} from "../../../utils/questionUtils";
import { strings } from "../../../resources/strings";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
  WorkflowName,
} from "../../../apis/API";
import { WorkflowItem, WorkflowSpec } from "./types";
import { buildWorkflowAnswers, parseRepeatableItems } from "./workflowAnswers";
import { resolveStructuredFormLabel } from "./workflowLabels";
import { getFieldError } from "./fieldValidation";
import { AnswerIcon, CHECK_PATH, saveButtonClass } from "./answerButtons";
import AnswerButtonRow from "./AnswerButtonRow";
import ChangeAnswerButton from "./ChangeAnswerButton";
import BoolContextCard from "../BoolContextCard";
import { FieldLabel, Row, StructuredRow, emptyValues } from "./StructuredForm";
import type { WorkflowAnswer } from "./WorkflowQuestion";

const wf = strings.reportForm.workflow;

export interface EmploymentFormProps {
  question: LocalizedParticipantQuestion;
  workflowName: WorkflowName;
  /** Resolved employment spec (YES_NO_STRUCTURED_FORM + `onFileListConfirm`). */
  spec: WorkflowSpec;
  /** Raw `questionPrepopulation.values` JSON — the on-file job list. */
  prepopulationValues?: string;
  /** Prior answer, for back-nav re-display. */
  defaultAnswer?: IdentifiableAnswerV3Input;
  onAnswer: (answer: WorkflowAnswer) => void;
}

/** Merge a parsed item over a blank row so every spec field key is present. */
const toRows = (spec: WorkflowSpec, items: WorkflowItem[]): Row[] => {
  const base = items.length > 0 ? items : [emptyValues(spec)];
  return base.map((values, i) => ({
    id: i,
    values: { ...emptyValues(spec), ...values },
  }));
};

/**
 * The repeatable multi-job entry form (scenario-2 blank, or scenario-1 "changed"
 * — prefilled only when >1 on-file job). Mirrors `StructuredForm`'s repeatable
 * form step (reusing its `StructuredRow`), but emits through the caller's
 * `onSave` so `EmploymentForm` can attach the `changed` key. Mounted fresh per
 * entry with its seed rows, so `useState` initializers pick up the right seed.
 */
const EmploymentEntry: React.FC<{
  spec: WorkflowSpec;
  fields: FieldLabel[];
  entryTitle: string;
  itemLabel: string;
  addMoreLabel: string;
  initialRows: Row[];
  onSave: (items: WorkflowItem[]) => void;
  onBack: () => void;
  /** Owning workflow — StructuredRow needs it to resolve autofill tokens. */
  workflowName: WorkflowName;
}> = ({
  spec,
  fields,
  entryTitle,
  itemLabel,
  addMoreLabel,
  initialRows,
  onSave,
  onBack,
  workflowName,
}) => {
  const [rows, setRows] = useState<Row[]>(initialRows);
  const nextId = useRef(initialRows.length);

  const handleChange = useCallback(
    (rowId: number, key: string, value: string) => {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId ? { ...r, values: { ...r.values, [key]: value } } : r,
        ),
      );
    },
    [],
  );

  const handleAddRow = () =>
    setRows((prev) => [
      ...prev,
      { id: nextId.current++, values: emptyValues(spec) },
    ]);

  // The Remove control only renders with ≥2 rows, so filtering leaves ≥1.
  const handleRemoveRow = (rowId: number) =>
    setRows((prev) => prev.filter((r) => r.id !== rowId));

  const requiredKeys = useMemo(
    () => spec.fields.filter((f) => f.required).map((f) => f.fieldKey),
    [spec],
  );
  // Droppable: no content in any required field (an unused "Add another" row).
  const rowIsDroppable = (r: Row) =>
    requiredKeys.every((k) => r.values[k].trim().length === 0);
  const rowFieldsAreValid = (r: Row) =>
    spec.fields.every(
      (f) => getFieldError(f.fieldKey, r.values[f.fieldKey]) === undefined,
    );

  const effectiveRows = rows.filter((r) => !rowIsDroppable(r));
  const canSave =
    effectiveRows.length > 0 &&
    effectiveRows.every(
      (r) =>
        requiredKeys.every((k) => r.values[k].trim().length > 0) &&
        rowFieldsAreValid(r),
    );
  const lastRow = rows[rows.length - 1];
  const canAddRow =
    requiredKeys.every((k) => lastRow.values[k].trim().length > 0) &&
    rowFieldsAreValid(lastRow);

  const handleSave = () =>
    onSave(
      effectiveRows.map((r) => {
        const item: WorkflowItem = {};
        for (const { fieldKey } of spec.fields) {
          item[fieldKey] = r.values[fieldKey].trim();
        }
        return item;
      }),
    );

  return (
    <div
      data-testid="employment-form"
      className="flex flex-col gap-6 overflow-y-auto"
    >
      <h2 className="text-2xl text-black">{entryTitle}</h2>

      {rows.map((row, rowIndex) => (
        <div key={row.id} className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-primary">
              {`${itemLabel} #${rowIndex + 1}`}
            </span>
            {rows.length > 1 && (
              <button
                type="button"
                data-testid={`structured-remove-${row.id}`}
                aria-label={`${wf.remove} ${rowIndex + 1}`}
                onClick={() => handleRemoveRow(row.id)}
                className="min-h-[44px] px-3 text-sm text-[#515964] underline"
              >
                {wf.remove}
              </button>
            )}
          </div>
          <StructuredRow
            fields={fields}
            values={row.values}
            rowId={row.id}
            onChange={handleChange}
            autoFocusFirst={rowIndex === 0}
            workflowName={workflowName}
          />
        </div>
      ))}

      <button
        type="button"
        data-testid="structured-add"
        onClick={handleAddRow}
        disabled={!canAddRow}
        className="min-h-[44px] self-start px-3 font-semibold text-primary disabled:opacity-30"
      >
        + {addMoreLabel}
      </button>

      <button
        type="button"
        data-testid="structured-save"
        onClick={handleSave}
        disabled={!canSave}
        className={saveButtonClass}
      >
        <AnswerIcon testId="save-continue-icon" path={CHECK_PATH} />
        {wf.saveContinue}
      </button>

      <ChangeAnswerButton onClick={onBack} />
    </div>
  );
};

/**
 * Read-only card for one on-file job on the "changes" step: the employer name
 * (or "Job #N" when blank) as a heading, with the remaining non-blank declared
 * fields shown as label:value rows.
 */
const OnFileJobCard: React.FC<{
  job: WorkflowItem;
  fields: FieldLabel[];
  fallbackName: string;
}> = ({ job, fields, fallbackName }) => {
  const header = job.employerName?.trim() ? job.employerName : fallbackName;
  const context: Record<string, string> = {};
  for (const f of fields) {
    if (f.key === "employerName") {
      continue; // shown as the card header
    }
    const value = job[f.key]?.trim();
    if (value) {
      context[f.label] = value;
    }
  }
  return (
    <div data-testid="employment-onfile-job" className="flex flex-col gap-2">
      <span className="font-semibold text-primary">{header}</span>
      {Object.keys(context).length > 0 && <BoolContextCard context={context} />}
    </div>
  );
};

/**
 * `employment` workflow UI (Android `EmploymentEditor`, EHA-885 — the reworked,
 * on-file-list-confirm employment screen). Three steps:
 *
 * - **root**: "Are you currently employed?" Yes/No. No → `answer:false` +
 *   `{employment:[]}`. Yes → `changes` when jobs are on file, else `entry`.
 * - **changes** (on-file jobs present): "Have there been changes to your jobs?"
 *   above the on-file jobs shown read-only. No → reconfirm → `answer:true` +
 *   `{changed:false, employment:[echoed on-file jobs]}`. Yes → `entry`.
 * - **entry**: the repeatable multi-job form. From `changes`-Yes it carries
 *   `changed:true` and prefills only when >1 on-file job (a lone job is likely
 *   stale); from the no-jobs `root`-Yes it carries no `changed` key and starts
 *   blank. → `answer:true` + `{employment:[…]}` (+ `changed` when applicable).
 *
 * `changed` is present ONLY when on-file jobs existed to confirm, matching the
 * Android wire contract + the officer-side renderer's discriminator.
 */
const EmploymentForm: React.FC<EmploymentFormProps> = ({
  question,
  workflowName,
  spec,
  prepopulationValues,
  defaultAnswer,
  onAnswer,
}) => {
  const questionId = question.id ?? "";
  const wd = question.workflowDefinition;
  const headlineEntry = resolveLocalizedEntry(question.localization);
  const headline = headlineEntry?.text ?? "";
  // SC 3.1.2 — the resolver falls back across languages, so text can arrive in
  // a language the document does not declare. undefined when it matches.
  const headlineLang = langAttribute(headlineEntry?.locale);

  const fields = useMemo<FieldLabel[]>(
    () =>
      spec.fields.map((f) => ({
        key: f.fieldKey,
        required: f.required,
        label:
          resolveStructuredFormLabel(workflowName, wd, "entry", f.fieldKey) ??
          f.fieldKey,
      })),
    [spec, workflowName, wd],
  );

  // On-file jobs from prepopulation (each item carries every spec field, missing
  // → ""). Only used when the participant has NOT already answered this session
  // (defaultAnswer takes precedence for back-nav).
  const onFileJobs = useMemo(
    () => parseRepeatableItems(prepopulationValues, spec, workflowName),
    [prepopulationValues, spec, workflowName],
  );
  const hasOnFileJobs = onFileJobs.length > 0;
  // Prior in-session answer's items (back-nav): when present they seed the entry
  // instead of the on-file jobs, so an in-session "No"/edit isn't overwritten.
  const priorItems = useMemo(
    () =>
      defaultAnswer
        ? parseRepeatableItems(
            defaultAnswer.workflowAnswers,
            spec,
            workflowName,
          )
        : null,
    [defaultAnswer, spec, workflowName],
  );

  const [step, setStep] = useState<"root" | "changes" | "entry">("root");
  // Whether the entry, when reached, is the confirm-flow "changed" branch (emits
  // `changed:true`) vs the no-jobs ask branch (no `changed` key).
  const [entryChanged, setEntryChanged] = useState(false);
  const [entryRows, setEntryRows] = useState<Row[]>([]);

  const rootAnswer = defaultAnswer?.answer ?? null;

  const changesTitle =
    resolveStructuredFormLabel(workflowName, wd, "changes", "title") ??
    headline;
  const entryTitle =
    resolveStructuredFormLabel(workflowName, wd, "entry", "title") ?? headline;
  const addMoreLabel =
    resolveStructuredFormLabel(workflowName, wd, "entry", "addMore") ??
    wf.addAnother;
  const itemLabel =
    resolveStructuredFormLabel(workflowName, wd, "entry", "itemLabel") ??
    "Item";

  const emit = (
    answer: boolean,
    items: WorkflowItem[],
    extra?: Record<string, unknown>,
  ) =>
    onAnswer({
      questionId,
      questionType: QuestionType.workflow,
      workflowName,
      answer,
      workflowAnswers: JSON.stringify({
        ...buildWorkflowAnswers(workflowName, spec, items),
        ...extra,
      }),
    });

  // Open the entry step, seeding rows: prior in-session items win (back-nav);
  // else the "changed" branch prefills from on-file jobs only when >1; else blank.
  const openEntry = (changed: boolean) => {
    let seed: WorkflowItem[];
    if (priorItems && priorItems.length > 0) {
      seed = priorItems;
    } else if (
      changed &&
      spec.onFileListConfirm?.prefillWhenMoreThanOne &&
      onFileJobs.length > 1
    ) {
      seed = onFileJobs;
    } else {
      seed = [];
    }
    setEntryChanged(changed);
    setEntryRows(toRows(spec, seed));
    setStep("entry");
  };

  const handleRootYes = () =>
    hasOnFileJobs ? setStep("changes") : openEntry(false);
  const handleRootNo = () => emit(false, []);

  // --- entry step ----------------------------------------------------------
  if (step === "entry") {
    return (
      <EmploymentEntry
        spec={spec}
        fields={fields}
        entryTitle={entryTitle}
        itemLabel={itemLabel}
        addMoreLabel={addMoreLabel}
        initialRows={entryRows}
        onSave={(items) =>
          emit(true, items, entryChanged ? { changed: true } : undefined)
        }
        onBack={() => setStep(hasOnFileJobs ? "changes" : "root")}
        workflowName={workflowName}
      />
    );
  }

  // --- changes step (on-file jobs present) ---------------------------------
  if (step === "changes") {
    return (
      <div data-testid="employment-form" className="flex flex-col gap-6">
        <h2 className="text-2xl text-black">{changesTitle}</h2>

        <div className="flex flex-col gap-4">
          {onFileJobs.map((job, i) => (
            <OnFileJobCard
              key={i}
              job={job}
              fields={fields}
              fallbackName={`${itemLabel} #${i + 1}`}
            />
          ))}
        </div>

        <AnswerButtonRow
          onYes={() => openEntry(true)}
          onNo={() => emit(true, onFileJobs, { changed: false })}
          yesSelected={false}
          noSelected={false}
          yesTestId="employment-changes-yes"
          noTestId="employment-changes-no"
        />

        <ChangeAnswerButton onClick={() => setStep("root")} />
      </div>
    );
  }

  // --- root step -----------------------------------------------------------
  return (
    <div data-testid="employment-form" className="flex flex-col gap-6">
      <h2 lang={headlineLang} className="text-2xl text-black">
        {headline}
      </h2>

      <AnswerButtonRow
        onYes={handleRootYes}
        onNo={handleRootNo}
        yesSelected={rootAnswer === true}
        noSelected={rootAnswer === false}
        yesTestId="structured-yes"
        noTestId="structured-no"
      />
    </div>
  );
};

export default EmploymentForm;
